import classNames from 'classnames';

import { PriceInterval, type Tier } from '@/types';
import { type CSSProperties, useMemo } from 'react';
import { currencyFormatter } from '../utils/currencyFormatter';
import { computeFont } from '../utils/fonts';
import * as styles from './Pricing.css';
import type { PricingElementProps } from './Pricing.types';
import GiftCheckoutForm from './components/GiftCheckoutForm';
import InternalCheckout from './components/InternalCheckout';
import { Tab } from './components/Tab';
import { Tabs } from './components/Tabs';
import TierCard from './components/TierCard';

export function PricingElement(props: PricingElementProps) {
  const {
    element,
    children,
    attributes,
    className,
    selectedTier,
    onTierClick,
    selectedPrice,
    onPriceClick,
    isStripeLoading,
    email,
    onEmailChange,
    showTaxIdField,
    taxId,
    onTaxIdChange,
    donationAmountCents,
    onDonationAmountCentsChange,
    isSubmitting,
    StripeCardElement,
    PaymentRequestButtonElement,
    isInternalCheckout,
    hasTermsOfService,
    hasCustomTermsOfService,
    currentFormMode,
    onCurrentFormModeChange,
    isLive,
    translations,
  } = props;

  const {
    mobile_hide,
    desktop_hide,
    customAttributes,
    padding,
    gap,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    cardShadow,
    cardBackgroundColor,
    cardPadding,
    nameColor,
    descriptionColor,
    nameSelectedColor,
    descriptionSelectedColor,
    featureSelectedColor,
    featureSelectedIconColor,
    priceColor,
    priceDenominatorColor,
    priceFontSize,
    priceDenominatorFontSize,
    offerPriceColor,
    offerPriceFontSize,
    offerDetailsColor,
    offerDetailsIconColor,
    featureColor,
    featureIconColor,
    buttonBorderRadius,
    buttonBorderWidth,
    buttonBorderColor,
    buttonBorderStyle,
    buttonColor,
    buttonTextColor,
    tabBackgroundColor,
    tabSelectedBackgroundColor,
    tabTextColor,
    tabSelectedTextColor,
    tabBorderRadius,
    tabBorderWidth,
    tabBorderColor,
    tabBorderStyle,
    tabsContainerBorderRadius,
    tabsContainerBorderWidth,
    tabsContainerBorderColor,
    tabsContainerBorderStyle,
    recommendedBackgroundColor,
    recommendedTextColor,
    recommendedBorderRadius,
    inputBackgroundColor,
    inputTextColor,
    inputPlaceholderColor,
    fontFamily,
    data,
    giftTiersData,
    isGiftingEnabled,
    giftFormHeadingTextColor,
    giftFormHeadingFontSize,
    giftFormLabelTextColor,
    giftFormLabelFontSize,
    giftTabHeadingText,
    giftTabHeadingFontSize,
    giftTabHeadingTextColor,
  } = element.attrs || {};

  // The value of memoized renderable tiers is derived keeping in
  // mind the two problems below

  // Problem
  // - A Tier can be disabled so it's not visible to the visitors
  // Solution
  // - Filter out the disabled tiers by asserting `tiers.enabled` attribute

  // Problem
  // - A Tier has many prices/intervals (monthly/yearly/donation/one-time)
  // - By default, if there is more than one price, they are shown as tabs
  //   in the same tier card
  // - Users can decide to split prices/intervals in a tier
  //   to display as separate cards
  // - When tier.should_split_prices is true, we display prices in that tier
  //   as separate cards
  // Solution
  // - Loop through filtered/enabled tiers and split prices if `tier.should_split_prices`
  //   is true
  // - Example; if a Tier has two prices, monthly and yearly, we will then
  //   have two tiers, with one price each. The tiers will be presented to the
  //   visitor as separate cards, one with monthly pricing and the other one
  //   with yearly pricing
  const renderableTiers: Tier[] = useMemo(() => {
    const alteredTiers: Tier[] = [];

    ((currentFormMode === 'gift' ? giftTiersData : data) || [])
      .filter((tier) => !!tier.enabled)
      .forEach((tier) => {
        if (tier.should_split_prices && tier.prices.length > 1) {
          tier.prices.forEach((price) => {
            alteredTiers.push({
              ...tier,
              prices: [price],
            });
          });
        } else {
          alteredTiers.push(tier);
        }
      });

    return alteredTiers;
  }, [data, giftTiersData, currentFormMode]);

  const evaluatedInternalCheckoutCta = useMemo(() => {
    if (currentFormMode === 'gift' && element.attrs?.giftFormCheckoutCta) {
      return element.attrs.giftFormCheckoutCta;
    }

    if (currentFormMode === 'individual' && element.attrs?.internalCheckoutCta) {
      return element.attrs.internalCheckoutCta;
    }

    const isDonation = selectedPrice?.interval === PriceInterval.DONATION;
    const amountCents = (isDonation ? donationAmountCents : selectedPrice?.amount_cents) || 0;

    return selectedPrice
      ? `Pay ${currencyFormatter({ currency: selectedPrice.currency }).format(amountCents / 100)}`
      : 'Submit';
  }, [element.attrs?.internalCheckoutCta, selectedPrice, donationAmountCents]);

  // Shows tabs to a visitor to chose an option between
  // buying the subscription as an individual or
  // as a gift for someone else
  const showFormModeTabs =
    (!isLive && isGiftingEnabled) ||
    (isGiftingEnabled &&
      giftTiersData &&
      Array.isArray(giftTiersData) &&
      giftTiersData.length > 0 &&
      currentFormMode &&
      onCurrentFormModeChange);

  return (
    <div
      className="relative w-fit"
      style={
        {
          // Card
          '--card-padding': cardPadding,
          '--card-background': cardBackgroundColor,
          '--card-shadow': cardShadow,
          '--card-height': '100%',

          '--name-color': nameColor,
          '--description-color': descriptionColor,
          '--feature-color': featureColor,
          '--feature-icon-color': featureIconColor,
          '--name-selected-color': nameSelectedColor,
          '--description-selected-color': descriptionSelectedColor,
          '--feature-selected-color': featureSelectedColor,
          '--feature-selected-icon-color': featureSelectedIconColor,
          '--price-color': priceColor,
          '--price-denominator-color': priceDenominatorColor,
          '--price-font-size': priceFontSize,
          '--price-denominator-font-size': priceDenominatorFontSize,
          '--offer-price-color': offerPriceColor,
          '--offer-price-font-size': offerPriceFontSize,
          '--offer-details-color': offerDetailsColor,
          '--offer-details-icon-color': offerDetailsIconColor,
          '--button-color': buttonColor,
          '--button-text-color': buttonTextColor,
          '--button-border-radius': buttonBorderRadius,
          '--button-border-color': buttonBorderColor,
          '--button-border-width': buttonBorderWidth,
          '--button-border-style': buttonBorderStyle,

          // Tab
          '--tab-background-color': tabBackgroundColor,
          '--tab-selected-background-color': tabSelectedBackgroundColor,
          '--tab-text-color': tabTextColor,
          '--tab-selected-text-color': tabSelectedTextColor,
          '--tab-border-radius': tabBorderRadius,
          '--tab-border-color': tabBorderColor,
          '--tab-border-width': tabBorderWidth,
          '--tab-border-style': tabBorderStyle,
          '--tabs-container-border-radius': tabsContainerBorderRadius,
          '--tabs-container-border-color': tabsContainerBorderColor,
          '--tabs-container-border-width': tabsContainerBorderWidth,
          '--tabs-container-border-style': tabsContainerBorderStyle,

          '--recommended-background-color': recommendedBackgroundColor,
          '--recommended-text-color': recommendedTextColor,
          '--recommended-border-radius': recommendedBorderRadius,
          '--input-background-color': inputBackgroundColor,
          '--input-text-color': inputTextColor,
          '--input-placeholder-color': inputPlaceholderColor,

          // Gift
          '--gift-tab-heading-text-color': giftTabHeadingTextColor,
          '--gift-tab-heading-font-size': giftTabHeadingFontSize,
          '--gift-form-heading-text-color': giftFormHeadingTextColor,
          '--gift-form-heading-font-size': giftFormHeadingFontSize,
          '--gift-form-label-text-color': giftFormLabelTextColor,
          '--gift-form-label-font-size': giftFormLabelFontSize,
        } as CSSProperties
      }
      {...attributes}
    >
      <div
        {...customAttributes}
        className={classNames(
          'flex flex-col mx-auto w-fit items-center',
          className,
          {
            'mobile:hidden': mobile_hide,
            'desktop:hidden': desktop_hide,
          },
          styles.pricingContainer,
          customAttributes?.className,
        )}
        style={{
          gap: gap,
          padding,
          borderStyle,
          borderWidth,
          borderRadius,
          borderColor,
          fontFamily: computeFont(fontFamily) || 'inherit',
        }}
      >
        {showFormModeTabs && (
          <Tabs>
            <Tab
              key="individual-tab"
              isSelected={currentFormMode === 'individual'}
              label={translations?.['upgrade.tabs.individual.title'] || 'Individual'}
              onClick={() => onCurrentFormModeChange?.('individual')}
            />
            <Tab
              key="gift-tab"
              isSelected={currentFormMode === 'gift'}
              label={translations?.['upgrade.tabs.gift.title'] || 'Gift'}
              onClick={() => onCurrentFormModeChange?.('gift')}
            />
          </Tabs>
        )}
        {currentFormMode === 'gift' && giftTabHeadingText ? (
          <h2 className={classNames(styles.giftTabHeading)}>{giftTabHeadingText}</h2>
        ) : null}
        <div
          className={classNames('flex flex-col sm:flex-row sm:flex-wrap sm:justify-center h-full')}
          style={
            {
              gap: gap,
              width: '100%',
            } as CSSProperties
          }
        >
          {renderableTiers.map((tier, index) => (
            <TierCard
              key={`${index}-${tier.id}-${currentFormMode}`}
              tier={tier}
              attributes={element.attrs}
              selectedTier={selectedTier}
              selectedPrice={selectedPrice}
              setSelectedTier={onTierClick}
              setSelectedPrice={onPriceClick}
              isInternalCheckout={isInternalCheckout}
              donationAmountCents={donationAmountCents}
              setDonationAmountCents={onDonationAmountCentsChange}
              isCtaDisabled={isStripeLoading || isSubmitting}
              currentFormMode={currentFormMode}
            />
          ))}
        </div>
        {currentFormMode === 'gift' && (
          <GiftCheckoutForm
            translations={translations}
            email={email}
            onEmailChange={onEmailChange}
            showTaxIdField={showTaxIdField}
            taxId={taxId}
            onTaxIdChange={onTaxIdChange}
            ctaText={evaluatedInternalCheckoutCta}
            isCtaDisabled={isStripeLoading || isSubmitting || !selectedTier}
            StripeCardElement={StripeCardElement}
            selectedTier={selectedTier}
            selectedPrice={selectedPrice}
            PaymentRequestButtonElement={PaymentRequestButtonElement}
            hasTermsOfService={hasTermsOfService}
            hasCustomTermsOfService={hasCustomTermsOfService}
            inputTextColor={inputTextColor}
            inputPlaceholderColor={inputPlaceholderColor}
            inputBackgroundColor={inputBackgroundColor}
            isInternalCheckout={isInternalCheckout}
          />
        )}
        {isInternalCheckout && currentFormMode === 'individual' && (
          <InternalCheckout
            email={email}
            onEmailChange={onEmailChange}
            showTaxIdField={showTaxIdField}
            taxId={taxId}
            onTaxIdChange={onTaxIdChange}
            ctaText={evaluatedInternalCheckoutCta}
            isCtaDisabled={isStripeLoading || isSubmitting || !selectedTier}
            StripeCardElement={StripeCardElement}
            selectedTier={selectedTier}
            selectedPrice={selectedPrice}
            PaymentRequestButtonElement={PaymentRequestButtonElement}
            hasTermsOfService={hasTermsOfService}
            hasCustomTermsOfService={hasCustomTermsOfService}
            inputTextColor={inputTextColor}
            inputPlaceholderColor={inputPlaceholderColor}
            inputBackgroundColor={inputBackgroundColor}
          />
        )}
      </div>
      {children}
    </div>
  );
}
